import { PmsProcedureTypeModel } from '../models/organizations/PmsLookupModels';

// One entry of the {procedureTypes} module field (ProcedureTypeModel in voicify-integrations).
// Extra flags set by hand (e.g. DefaultProcedureForExistingPatients) are kept as-is when merging.
export interface ProcedureTypeEntry {
    procedureTypeId?: string;
    procedureTypeDescription?: string;
    appointmentDuration?: number;
    additionalProcedureTypeIdsToLookUp?: string[] | null;
    isCleaningAppointment?: boolean;
    isEmergencyType?: boolean;
    [key: string]: any;
}

export type ProposalRowStatus = 'new' | 'existing' | 'not-in-pms';

export interface ProcedureTypeProposalRow {
    procedureTypeId: string;
    pmsName?: string;
    status: ProposalRowStatus;
    include: boolean;
    entry: ProcedureTypeEntry;
    // Set when this type is the location's new patient cleaning, existing patient cleaning or emergency appointment
    role?: CoreProcedureRole;
}

// Same patterns the Location Manager warns about when the matching flag is missing
const CLEANING_PATTERN = /\b(cleaning|hygiene)\b/i;
const EMERGENCY_PATTERN = /\bemergenc(?:y|ies)\b/i;

// Reads the stored field value; returns [] for empty values and null when it isn't a JSON array
export const parseProcedureTypes = (raw?: string | null): ProcedureTypeEntry[] | null => {
    const trimmed = (raw || '').trim();
    if (!trimmed || trimmed === '-1' || /^\{procedureTypes\}$/i.test(trimmed)) {
        return [];
    }
    try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed.filter(item => item && typeof item === 'object') : null;
    } catch (e) {
        return null;
    }
};

const newEntryFromPms = (type: PmsProcedureTypeModel): ProcedureTypeEntry => {
    const entry: ProcedureTypeEntry = {
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: type.durationMinutes,
        procedureTypeDescription: type.name,
        procedureTypeId: type.id
    };
    if (CLEANING_PATTERN.test(type.name)) entry.isCleaningAppointment = true;
    if (EMERGENCY_PATTERN.test(type.name)) entry.isEmergencyType = true;
    return entry;
};

/**
 * Merges the PMS procedure types into the existing field value by ID: existing entries keep their
 * descriptions and flags (missing durations are filled from the PMS), new PMS types are added with
 * defaults, and existing entries the PMS no longer returns are kept so they can be reviewed.
 * New types start unselected unless the field was empty, so a merge never silently grows the list.
 */
export const buildProcedureTypeProposal = (pmsTypes: PmsProcedureTypeModel[], existing: ProcedureTypeEntry[]): ProcedureTypeProposalRow[] => {
    const existingById = new Map<string, ProcedureTypeEntry>();
    existing.forEach(entry => {
        if (entry.procedureTypeId != null) existingById.set(String(entry.procedureTypeId), entry);
    });
    const pmsIds = new Set(pmsTypes.map(type => type.id));

    const rows: ProcedureTypeProposalRow[] = pmsTypes.map(type => {
        const current = existingById.get(type.id);
        if (current) {
            return {
                procedureTypeId: type.id,
                pmsName: type.name,
                status: 'existing',
                include: true,
                entry: { ...current, appointmentDuration: current.appointmentDuration ?? type.durationMinutes }
            };
        }
        return {
            procedureTypeId: type.id,
            pmsName: type.name,
            status: 'new',
            include: false,
            entry: newEntryFromPms(type)
        };
    });

    existing
        .filter(entry => entry.procedureTypeId == null || !pmsIds.has(String(entry.procedureTypeId)))
        .forEach(entry => rows.push({
            procedureTypeId: entry.procedureTypeId != null ? String(entry.procedureTypeId) : '',
            status: 'not-in-pms',
            include: true,
            entry
        }));

    return rows;
};

export type CoreProcedureRole = 'new-patient-cleaning' | 'existing-patient-cleaning' | 'emergency';

interface CoreProcedureRoleDefinition {
    role: CoreProcedureRole;
    label: string;
    defaultDescription: string;
    flags: ProcedureTypeEntry;
    // Picks a likely PMS type by name when nothing is set yet
    guess: (name: string) => boolean;
    // Recognizes an existing entry already playing this role (flag names are case-insensitive in the backend)
    matchesExisting: (entry: ProcedureTypeEntry) => boolean;
}

export const flagIsSet = (entry: ProcedureTypeEntry, flag: string) =>
    Object.keys(entry).some(key => key.toLowerCase() === flag.toLowerCase() && entry[key] === true);

// Sets or clears a flag, removing every case variant first so an IsEmergencyType key can't survive unchecking
export const setEntryFlag = (entry: ProcedureTypeEntry, flag: string, checked: boolean): ProcedureTypeEntry => {
    const result: ProcedureTypeEntry = { ...entry };
    Object.keys(result).forEach(key => { if (key.toLowerCase() === flag.toLowerCase()) delete result[key]; });
    // Leave the flag off the JSON entirely when unchecked, like hand-written entries
    if (checked) result[flag] = true;
    return result;
};

const isCleaningName = (name: string) => /(clean|hygiene|prophy|recall|check[- ]?up|routine)/i.test(name);

// Every location should offer these three; the backend uses the default flags to pick a type for new and existing patients
export const CORE_PROCEDURE_ROLES: CoreProcedureRoleDefinition[] = [
    {
        role: 'new-patient-cleaning',
        label: 'New patient cleaning',
        defaultDescription: "A first cleaning and exam for new patients who haven't visited the practice before.",
        flags: { isCleaningAppointment: true, defaultProcedureForNewPatients: true, assumePatientIsNewIfSelected: true },
        guess: name => /new\s*(patient|pt)/i.test(name) && (isCleaningName(name) || /exam/i.test(name)),
        matchesExisting: entry => flagIsSet(entry, 'defaultProcedureForNewPatients')
    },
    {
        role: 'existing-patient-cleaning',
        label: 'Existing patient cleaning',
        defaultDescription: 'A routine cleaning, check-up or hygiene visit for existing patients.',
        flags: { isCleaningAppointment: true, defaultProcedureForExistingPatients: true, assumePatientIsExistingIfSelected: true },
        guess: name => isCleaningName(name) && !/new\s*(patient|pt)|child|kid|pediatric/i.test(name),
        matchesExisting: entry => flagIsSet(entry, 'defaultProcedureForExistingPatients')
    },
    {
        role: 'emergency',
        label: 'Emergency appointment',
        defaultDescription: 'Any emergency appointment, including severe tooth pain, dental abscess or infection, knocked-out tooth, broken or cracked tooth, lost filling or crown, extraction, etc.',
        flags: { isEmergencyType: true },
        guess: name => EMERGENCY_PATTERN.test(name),
        matchesExisting: entry => flagIsSet(entry, 'isEmergencyType')
    }
];

export interface CoreProcedureAssignment {
    role: CoreProcedureRole;
    // Empty when the location doesn't offer this appointment
    procedureTypeId: string;
    entry: ProcedureTypeEntry;
    // The existing field entry this came from, so it isn't listed again under other types
    source?: ProcedureTypeEntry;
}

export const buildCoreEntry = (role: CoreProcedureRole, type?: PmsProcedureTypeModel, base?: ProcedureTypeEntry): ProcedureTypeEntry => {
    const definition = CORE_PROCEDURE_ROLES.find(d => d.role === role);
    if (base) {
        return { ...base, ...definition.flags, procedureTypeId: type?.id ?? base.procedureTypeId, appointmentDuration: base.appointmentDuration ?? type?.durationMinutes };
    }
    return {
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: type?.durationMinutes,
        procedureTypeDescription: definition.defaultDescription,
        procedureTypeId: type?.id,
        ...definition.flags
    };
};

/**
 * Fills the three core appointments: from an existing entry already flagged for that role (keeping
 * its description), otherwise from a PMS type whose name fits, otherwise left unassigned.
 */
export const buildCoreAssignments = (pmsTypes: PmsProcedureTypeModel[], existing: ProcedureTypeEntry[]): CoreProcedureAssignment[] => {
    const used = new Set<ProcedureTypeEntry>();
    const usedGuesses = new Set<string>();
    const fromExisting = (role: CoreProcedureRole, match: ProcedureTypeEntry): CoreProcedureAssignment => {
        used.add(match);
        usedGuesses.add(String(match.procedureTypeId));
        const type = pmsTypes.find(t => t.id === String(match.procedureTypeId));
        return { role, procedureTypeId: String(match.procedureTypeId ?? ''), entry: buildCoreEntry(role, type, match), source: match };
    };
    const available = (entry: ProcedureTypeEntry) => !used.has(entry) && entry.procedureTypeId != null;

    // Flagged entries are definite, so claim those for every role before falling back to guesses
    const flagged = CORE_PROCEDURE_ROLES.map(definition => {
        const match = existing.find(entry => available(entry) && definition.matchesExisting(entry));
        return match ? fromExisting(definition.role, match) : null;
    });

    return CORE_PROCEDURE_ROLES.map((definition, index) => {
        if (flagged[index]) return flagged[index];
        // An existing entry whose description fits, e.g. "routine visit, regular cleaning" for existing patients
        const described = existing.find(entry => available(entry) && definition.guess(entry.procedureTypeDescription || ''));
        if (described) return fromExisting(definition.role, described);

        const guess = pmsTypes.find(type => type.isBookable !== false && !usedGuesses.has(type.id) && definition.guess(type.name))
            ?? pmsTypes.find(type => !usedGuesses.has(type.id) && definition.guess(type.name));
        if (!guess) return { role: definition.role, procedureTypeId: '', entry: null };
        usedGuesses.add(guess.id);
        // Keep a hand-written entry for the same PMS type rather than listing it twice
        const sameType = existing.find(entry => available(entry) && String(entry.procedureTypeId) === guess.id);
        return sameType ? fromExisting(definition.role, sameType) : { role: definition.role, procedureTypeId: guess.id, entry: buildCoreEntry(definition.role, guess) };
    });
};

const withoutKeys = (entry: ProcedureTypeEntry, keys: string[]): ProcedureTypeEntry => {
    const lowerKeys = keys.map(key => key.toLowerCase());
    return Object.keys(entry).reduce((result, key) => {
        if (lowerKeys.indexOf(key.toLowerCase()) < 0) result[key] = entry[key];
        return result;
    }, {} as ProcedureTypeEntry);
};

// Flags that only mean "this is the default for new/existing patients"; cleaning and emergency flags stay with the type
const roleOnlyFlags = (role: CoreProcedureRole) => Object.keys(CORE_PROCEDURE_ROLES.find(d => d.role === role).flags)
    .filter(flag => flag !== 'isCleaningAppointment' && flag !== 'isEmergencyType');

/**
 * Moves an entry from one main-appointment role to another (or to none): drops the old role's default flags,
 * adds the new role's flags, and uses the role's description when the entry only had the PMS name.
 */
export const applyRoleToEntry = (entry: ProcedureTypeEntry, from: CoreProcedureRole | undefined, to: CoreProcedureRole | undefined, pmsName?: string): ProcedureTypeEntry => {
    let next = from ? withoutKeys(entry, roleOnlyFlags(from)) : { ...entry };
    if (!to) return next;
    const definition = CORE_PROCEDURE_ROLES.find(d => d.role === to);
    next = { ...withoutKeys(next, Object.keys(definition.flags)), ...definition.flags };
    const description = (next.procedureTypeDescription || '').trim();
    const isPlaceholderDescription = !description || description === pmsName || CORE_PROCEDURE_ROLES.some(d => d.defaultDescription === description);
    if (isPlaceholderDescription) next.procedureTypeDescription = definition.defaultDescription;
    return next;
};

/**
 * Puts the suggested main appointments onto the rows for those PMS types, so every type stays in one list.
 * Each role ends up on at most one row, which is selected.
 */
export const assignCoreRoles = (rows: ProcedureTypeProposalRow[], core: CoreProcedureAssignment[]): ProcedureTypeProposalRow[] => {
    const result = rows.map(row => ({ ...row }));
    core.filter(assignment => assignment.procedureTypeId && assignment.entry).forEach(assignment => {
        const row = result.find(r => !r.role && (assignment.source ? r.entry === assignment.source : r.procedureTypeId === assignment.procedureTypeId))
            ?? result.find(r => !r.role && r.procedureTypeId === assignment.procedureTypeId);
        if (row) {
            row.role = assignment.role;
            row.include = true;
            row.entry = assignment.entry;
        }
    });
    return result;
};

export const proposalToEntries = (rows: ProcedureTypeProposalRow[]): ProcedureTypeEntry[] =>
    rows.filter(row => row.include).map(row => row.entry);

export const formatProcedureTypesJson = (entries: ProcedureTypeEntry[]): string => JSON.stringify(entries, null, 2);

// Problems that would block the assistant from using an entry, or that the Location Manager flags
export const getProposalWarnings = (entries: ProcedureTypeEntry[]): string[] => {
    const warnings: string[] = [];
    if (entries.length === 0) warnings.push('No procedure types selected.');
    entries.forEach(entry => {
        const label = entry.procedureTypeDescription || entry.procedureTypeId || 'An entry';
        if (!entry.procedureTypeDescription?.trim()) warnings.push(`${entry.procedureTypeId}: add a description.`);
        if (entry.appointmentDuration == null || isNaN(Number(entry.appointmentDuration))) warnings.push(`${label}: add a duration.`);
        if (CLEANING_PATTERN.test(entry.procedureTypeDescription || '') && entry.isCleaningAppointment !== true) {
            warnings.push(`${label}: mentions cleaning or hygiene but isn't marked as a cleaning appointment.`);
        }
        if (EMERGENCY_PATTERN.test(entry.procedureTypeDescription || '') && entry.isEmergencyType !== true) {
            warnings.push(`${label}: mentions emergencies but isn't marked as an emergency type.`);
        }
    });
    return warnings;
};
